import { BaseModule } from '@core/base/module/base-module'
import { AssetsService } from '@core/services/api-gateway'
import { TActiveIntegrationTypes } from '../integrations/integrations'
import { integrationDetailsByType } from '@ui/drawer/integration/utils/utils'
import { UnifiedFilterModule } from '@core/modules/filters/unified-filter.module'
import { unifiedIssuesConfig } from './issues.filters'

interface IIssuesState extends IRemoteData {
    data: TIssue[]
    inProcessIssues: Set<string>
}

const state: IIssuesState = {
    data: [],
    lastFetched: 0,
    loading: false,
    error: false,
    inProcessIssues: new Set<string>(),
}

class Issues extends BaseModule<IIssuesState> {
    filterModule: UnifiedFilterModule

    constructor(state: IIssuesState) {
        super(state, 'issues.module')
        this.filterModule = new UnifiedFilterModule(
            unifiedIssuesConfig,
            'issues.unified.filter'
        )
    }

    async loadIssues(
        forced?: boolean,
        polling?: boolean
    ): Promise<TIssue[] | boolean> {
        const store = this.store

        if (!store.get().lastFetched || forced) {
            this.setLoading(true)
            const [result, resultV2] = await Promise.all([
                AssetsService.getIssues(polling),
                AssetsService.getIssuesByType(polling),
            ])

            if (!result && !resultV2) {
                this.setLoading(false)
                this.setError(true)
                return false
            }

            const issues = (result ?? []) as TIssue[]
            const v2Issues = (resultV2 ?? []) as TIssue[]

            const allIssues = [...issues, ...v2Issues]

            this.setData(allIssues)
            this.setLastFetched()
            this.setLoading(false)
            return allIssues
        }
        return store.get().data
    }

    setData(data: TIssue[]) {
        this.store.setKey('data', data)
    }

    setLoading(loading: boolean) {
        this.store.setKey('loading', loading)
    }

    setError(error: boolean) {
        this.store.setKey('error', error)
    }

    setLastFetched() {
        this.store.setKey('lastFetched', Date.now())
    }

    getIssuesWithSubscription({ disableFilters = false }): TIssue[] {
        const data = this.store.get().data
        return this.filterModule.processData(data, disableFilters)
    }

    getIssues({ disableFilters = false }): TIssue[] {
        const data = this.store.get().data
        return this.filterModule.processData(data, disableFilters)
    }

    getIntegrationIssuesById(
        type: TActiveIntegrationTypes,
        integrationId: string | undefined
    ) {
        const data = this.store.get().data
        const { assetId } = integrationDetailsByType[type] ?? {}

        const integrationIssues = data
            .filter((issue: TIssue) =>
                issue.assets?.some((asset: Partial<TAsset>) => {
                    if (asset?.id) {
                        return (
                            asset.asset_type === 'integration' &&
                            (asset.id === assetId || asset.id === integrationId)
                        )
                    }
                    return false
                })
            )
            .sort(unifiedIssuesConfig.sortStrategy)

        return integrationIssues
    }

    getIssueById(
        id: string,
        useSubscription: boolean = false
    ): TIssue | undefined {
        const store = useSubscription ? this.useStore() : this.store.get()
        return store.data.find((issue: TIssue) => issue.id === id)
    }

    getInProcessIssuesIds() {
        const { inProcessIssues } = this.store.get()
        return inProcessIssues
    }

    setInProcessIssuesIds(ids: string[]) {
        this.setLoading(true)
        this.store.setKey('inProcessIssues', new Set(ids))
        this.setLoading(false)
    }

    updateInProcessIssuesIds(ids: string[]) {
        this.setLoading(true)
        const currentInProcessIssues = this.getInProcessIssuesIds()
        if (
            currentInProcessIssues.size !== ids.length ||
            ids.some((id) => !currentInProcessIssues.has(id))
        ) {
            this.setInProcessIssuesIds(ids)
        }
        this.setLoading(false)
    }

    addInProcessIssueId(id: string) {
        this.setLoading(true)
        const inProcessIssuesIds = this.getInProcessIssuesIds()
        if (!inProcessIssuesIds.has(id)) {
            const updatedSet = new Set(inProcessIssuesIds)
            updatedSet.add(id)
            this.store.setKey('inProcessIssues', updatedSet)
        }
        this.setLoading(false)
    }

    removeInProcessIssueId(id: string) {
        this.setLoading(true)
        const inProcessIssuesIds = this.getInProcessIssuesIds()
        if (inProcessIssuesIds.has(id)) {
            const updatedSet = new Set(inProcessIssuesIds)
            updatedSet.delete(id)
            this.store.setKey('inProcessIssues', updatedSet)
        }
        this.setLoading(false)
    }
}

export const IssuesModule = new Issues(state)
export type TIssuesModule = typeof IssuesModule
